import {ChangeDetectionStrategy, Component, computed, input, signal} from '@angular/core';
import {TranslatePipe} from '@brunata-angular/util';
import {SectionToggleComponent} from '../shared/ui/section-toggle/section-toggle.component';
import {StepFocusRingDirective} from '../steps/step-focus/step-focus-ring.directive';
import {FilePreviewContent} from './file-preview.model';
import {ExcelPreviewComponent} from './excel-preview/excel-preview.component';
import {PreviewLinesComponent} from './preview-lines/preview-lines.component';

const fileIcons: Record<FilePreviewContent['kind'], string> = {
    csv: 'bi-filetype-csv',
    excel: 'bi-filetype-xlsx',
    json: 'bi-filetype-json',
};

@Component({
    selector: 'app-file-preview',
    standalone: true,
    host: {class: 'card mb-4 d-block'},
    hostDirectives: [StepFocusRingDirective],
    imports: [ExcelPreviewComponent, PreviewLinesComponent, SectionToggleComponent, TranslatePipe],
    template: `
        <div class="file-preview d-flex flex-column">
            <app-section-toggle [(expanded)]="expanded" controls="file-preview-body">
                <span
                    class="preview-mark d-inline-flex align-items-center justify-content-center flex-shrink-0 text-primary"
                    aria-hidden="true">
                    <i class="bi bi-eye"></i>
                </span>
                <span class="fw-bold">
                    {{ 'customizedexports_export_preview_title' | translate: 'online' : 'export' }}
                </span>
            </app-section-toggle>

            @if (expanded()) {
                <div id="file-preview-body" class="px-4 pb-4">
                    <div class="file-frame">
                        <div
                            class="file-tab d-flex align-items-center gap-2 px-3 small text-secondary">
                            <i class="bi text-primary" [class]="fileIcon()" aria-hidden="true"></i>
                            <span class="font-monospace text-truncate">{{ fileName() }}</span>
                        </div>
                        <div class="file-body">
                            @if (content(); as content) {
                                @switch (content.kind) {
                                    @case ('excel') {
                                        <app-excel-preview
                                            [header]="content.header"
                                            [rows]="content.rows" />
                                    }
                                    @default {
                                        <app-preview-lines [lines]="content.lines" />
                                    }
                                }
                            }
                        </div>
                    </div>
                </div>
            }
        </div>
    `,
    styles: `
        .file-preview {
            --section-toggle-padding: 1.5rem;
            --preview-hairline: rgba(var(--bs-primary-rgb), 0.12);
            --preview-gutter: color-mix(in srgb, var(--bs-primary) 4%, var(--bs-white));
            --preview-gutter-width: 2.75rem;
            --preview-row-height: 1.75rem;
        }

        .file-frame {
            border: 1px solid var(--preview-hairline);
        }

        .file-tab {
            height: 2.25rem;
            border-bottom: 1px solid var(--preview-hairline);
            background-color: var(--preview-gutter);
        }

        .file-body {
            height: 20rem;
            overflow: auto;
            background:
                linear-gradient(
                        to right,
                        transparent calc(var(--preview-gutter-width) - 1px),
                        var(--preview-hairline) 0,
                        var(--preview-hairline) var(--preview-gutter-width),
                        transparent 0
                    )
                    no-repeat,
                linear-gradient(
                        to right,
                        var(--preview-gutter) var(--preview-gutter-width),
                        transparent 0
                    )
                    no-repeat;
        }

        .preview-mark {
            width: 1.75rem;
            height: 1.75rem;
            border: 1px dashed rgba(var(--bs-primary-rgb), 0.3);
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilePreviewComponent {
    readonly content = input.required<FilePreviewContent>();
    readonly fileName = input.required<string | null>();

    protected readonly expanded = signal(false);

    protected readonly fileIcon = computed(() => fileIcons[this.content().kind]);
}
